import {
  memo,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { FilterOption } from "@/components/FilterSelect";

/** Matches the API's per-filter list cap (shared/params.ts MAX_LIST_VALUES). */
export const MAX_FILTER_SELECTIONS = 20;

/**
 * Multi-select sibling of FilterSelect: same typeahead combobox, but rows
 * toggle checkboxes and the list stays open across picks. Empty selection =
 * "no filter" — clearable from the × in the field or the allLabel row.
 * Closed, the input summarizes the selection ("3 selected"); selections are
 * capped at MAX_FILTER_SELECTIONS to match the API's per-filter limit.
 */
export const MultiFilterSelect = memo(function MultiFilterSelect({
  label,
  allLabel,
  values,
  onChange,
  options,
  disabled,
}: {
  label: string;
  allLabel: string;
  values: string[];
  onChange: (values: string[]) => void;
  options: FilterOption[];
  disabled?: boolean;
}) {
  const id = useId();
  const listboxId = `${id}-listbox`;
  const [open, setOpen] = useState(false);
  // null = not editing; the input shows the selection summary.
  const [query, setQuery] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(() => new Set(values), [values]);
  const atCap = selected.size >= MAX_FILTER_SELECTIONS;

  const summary =
    values.length === 0
      ? ""
      : values.length === 1
        ? (options.find((o) => o.value === values[0])?.label ?? values[0])
        : `${values.length} selected`;

  const filtered = useMemo(() => {
    const all = [{ value: "", label: allLabel }, ...options];
    const needle = query?.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((o) => o.label.toLowerCase().includes(needle));
  }, [options, allLabel, query]);

  // Keep the highlight on a real row as typing narrows the list.
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const close = () => {
    setOpen(false);
    setQuery(null);
  };

  const toggle = (option: FilterOption) => {
    if (option.value === "") {
      onChange([]);
      return;
    }
    if (selected.has(option.value)) {
      onChange(values.filter((v) => v !== option.value));
    } else if (!atCap) {
      onChange([...values, option.value]);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setActiveIndex((i) =>
        e.key === "ArrowDown"
          ? Math.min(i + 1, filtered.length - 1)
          : Math.max(i - 1, 0),
      );
    } else if (e.key === "Enter") {
      if (open && filtered[activeIndex]) {
        e.preventDefault();
        toggle(filtered[activeIndex]);
      }
    } else if (e.key === "Escape") {
      close();
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-xs font-medium text-muted-foreground">
        {label}
      </label>
      <div className="relative w-64 max-w-full">
        <input
          id={id}
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-activedescendant={
            open && filtered[activeIndex] ? `${id}-opt-${activeIndex}` : undefined
          }
          aria-autocomplete="list"
          autoComplete="off"
          disabled={disabled}
          placeholder={allLabel}
          value={query ?? summary}
          onFocus={(e) => {
            setOpen(true);
            e.target.select();
          }}
          onBlur={close}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
          className="h-9 w-full rounded-md border border-input bg-background pl-3 pr-8 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
        />
        {values.length > 0 && !disabled ? (
          <button
            type="button"
            aria-label={`Clear ${label} filter`}
            title={`Clear ${label} filter`}
            // mousedown so the input's blur doesn't close the list first and
            // swallow the click.
            onMouseDown={(e) => {
              e.preventDefault();
              onChange([]);
              close();
            }}
            className="absolute right-1.5 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-sm text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : (
          <ChevronsUpDown
            aria-hidden
            className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
          />
        )}
        {open ? (
          <ul
            id={listboxId}
            role="listbox"
            aria-label={label}
            aria-multiselectable
            // w-max grows the flyout beyond the field so long option names
            // stay readable; the max-w cap keeps it on screen, and labels
            // wrap below rather than truncate.
            className="absolute z-50 mt-1 max-h-64 w-max min-w-full max-w-[min(28rem,calc(100vw-2rem))] overflow-auto rounded-md border bg-card py-1 text-sm shadow-md"
          >
            {filtered.length === 0 ? (
              <li className="px-3 py-1.5 text-muted-foreground">No matches</li>
            ) : (
              filtered.map((option, i) => {
                const isAll = option.value === "";
                const isSelected = isAll
                  ? values.length === 0
                  : selected.has(option.value);
                const isBlocked = !isAll && !isSelected && atCap;
                return (
                  <li
                    key={`${option.value}-${i}`}
                    id={`${id}-opt-${i}`}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={isBlocked || undefined}
                    ref={
                      i === activeIndex
                        ? (el) => el?.scrollIntoView({ block: "nearest" })
                        : undefined
                    }
                    // mousedown (not click) so the input doesn't blur first.
                    onMouseDown={(e) => {
                      e.preventDefault();
                      toggle(option);
                    }}
                    onMouseMove={() => setActiveIndex(i)}
                    className={cn(
                      "flex cursor-pointer items-center gap-2 px-3 py-1.5",
                      i === activeIndex && "bg-accent text-accent-foreground",
                      isBlocked && "cursor-not-allowed opacity-40",
                    )}
                  >
                    {isAll ? (
                      <span aria-hidden className="h-3.5 w-3.5 shrink-0" />
                    ) : (
                      <span
                        aria-hidden
                        // Explicit 3px: the theme's rounded-sm is 8px, which
                        // would round a 14px box into a radio-looking circle.
                        className={cn(
                          "flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-[3px] border",
                          isSelected
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-input",
                        )}
                      >
                        {isSelected ? <Check className="h-3 w-3" /> : null}
                      </span>
                    )}
                    <span className="min-w-0 break-words">{option.label}</span>
                  </li>
                );
              })
            )}
            {atCap ? (
              <li
                aria-hidden
                className="border-t px-3 py-1.5 text-xs text-muted-foreground"
              >
                Up to {MAX_FILTER_SELECTIONS} selections
              </li>
            ) : null}
          </ul>
        ) : null}
      </div>
    </div>
  );
});
